import * as React from 'react';

import _ from 'underscore';

import type {FilterObject} from 'nutshell-core/types';
import {isRange} from 'nutshell-core/utils/period-checks';

import {Radio} from 'shells/radio/radio';
import {RadioGroup} from 'shells/radio/radio-group';
import {TextField} from 'shells/textfield/textfield';

import styles from './tiered-radio-button-form.module.css';

const NULL_FIELD_KEY = 'null';
const ANY_FIELD_KEY = 'any';
const BETWEEN_FIELD_KEY = 'Between';

const SPECIAL_FIELDS = [NULL_FIELD_KEY, ANY_FIELD_KEY, BETWEEN_FIELD_KEY];
const SPECIAL_FIELD_VALUES = [NULL_FIELD_KEY, ANY_FIELD_KEY];

type Props = {
    value?: any;
    name: string;
    onChange: (...args: any) => any;
    nullOptionText?: string;
    anyOptionText?: string;
    isNumber: boolean;
    getFields?: FieldGetter;
};
type State = {
    selectedValue?: string;
    value: string;
};

export type FieldGetter = () => {
    id: string;
    value: string;
}[];

// @ts-expect-error
function getValue({selected, data}) {
    return selected === BETWEEN_FIELD_KEY ? data.join(' TO ') : data;
}

export class TieredRadioButtonForm extends React.PureComponent<Props, State> {
    onChangeDebounced: (arg1?: FilterObject | null | undefined) => any;

    constructor(props: Props) {
        super(props);
        this.state = {
            selectedValue: props.value ? props.value.selected : undefined,
            value: props.value ? getValue(props.value) : '',
        };

        this.onChangeDebounced = _.debounce((data) => {
            this.props.onChange(data);
        }, 500);
    }

    getFields: FieldGetter = () => {
        const fields = [];

        if (this.props.nullOptionText) {
            fields.push({id: 'null', value: this.props.nullOptionText});
        }

        if (this.props.anyOptionText) {
            fields.push({id: 'any', value: this.props.anyOptionText});
        }

        return fields;
    };

    render() {
        // @ts-expect-error
        const fieldMapFunction = (field) => {
            return (
                <div key={field.id}>
                    <Radio value={field.id}>{field.value}</Radio>
                    {this.state.selectedValue === field.id &&
                    // @ts-expect-error
                    !SPECIAL_FIELDS.includes(this.state.selectedValue) ? (
                        <div className={styles['textfield-container']}>
                            <TextField
                                // @ts-expect-error
                                className='ui-input--full-width'
                                autoFocus={true}
                                onChange={this.handleTextChange}
                                value={this.state.value}
                                type={this.props.isNumber ? 'number' : undefined}
                            />
                        </div>
                    ) : undefined}
                    {this.state.selectedValue === field.id &&
                    this.state.selectedValue === BETWEEN_FIELD_KEY ? (
                        <div className={styles['textfield-container--horizontal']}>
                            <TextField
                                // @ts-expect-error
                                autoFocus={true}
                                onChange={this.handleBetweenMinChange}
                                value={this.splitBetweenValue(this.state.value)[0]}
                                type={this.props.isNumber ? 'number' : undefined}
                            />
                            <span className={styles['between-divider']}>and</span>
                            <TextField
                                onChange={this.handleBetweenMaxChange}
                                value={this.splitBetweenValue(this.state.value)[1]}
                                type={this.props.isNumber ? 'number' : undefined}
                            />
                        </div>
                    ) : undefined}
                </div>
            );
        };

        const fields = this.props.getFields ? this.props.getFields() : this.getFields();

        return (
            <RadioGroup
                selectedValue={this.state.selectedValue}
                name={this.props.name}
                onChange={this.handleRadioSelect}
            >
                {fields.map(fieldMapFunction)}
            </RadioGroup>
        );
    }

    splitBetweenValue = (val?: string | null) => {
        if (!val) {
            return ['', ''];
        }

        const splitStr = val.split(' TO ');
        if (splitStr.length === 2) {
            return [splitStr[0], splitStr[1]];
            // @ts-expect-error
        } else if (splitStr[0].length) {
            return [splitStr[0], ''];
        } else {
            return ['', splitStr[1]];
        }
    };

    handleBetweenMinChange = (val: string) => {
        const splitValues = this.splitBetweenValue(this.state.value);

        this.handleTextChange(`${val} TO ${splitValues[1]}`);
    };

    handleBetweenMaxChange = (val: string) => {
        const splitValues = this.splitBetweenValue(this.state.value);

        this.handleTextChange(`${splitValues[0]} TO ${val}`);
    };

    handleRadioSelect = (val: string) => {
        if (val !== this.state.selectedValue) {
            this.setState({
                selectedValue: val,
                value: val === BETWEEN_FIELD_KEY ? ' TO ' : '',
            });

            if (SPECIAL_FIELD_VALUES.includes(val)) {
                const dataToSubmit = {
                    [this.props.name]: {
                        selected: val,
                        data: null,
                    },
                } as const;
                this.props.onChange(dataToSubmit);
            } else {
                // no-op
            }
        }
    };

    handleTextChange = (val: string) => {
        this.setState({value: val});

        if (!val.length) {
            this.onChangeDebounced(null);

            return;
        }

        const dataToSubmit = {
            [this.props.name]: {
                selected: this.state.selectedValue,
                data: val,
            },
        } as const;

        if (this.state.selectedValue !== BETWEEN_FIELD_KEY) {
            this.onChangeDebounced(dataToSubmit);
        } else if (this.state.selectedValue && isRange(val)) {
            this.onChangeDebounced(dataToSubmit);
        }
    };
}
